import { z } from "zod";

import type { AnyView, ViewID, VersionString } from "@bestowinc/tinkertoy-core";

import {
  BundleImportError,
  importBundle,
  importBundleModule,
} from "../bundle-loader/import-bundle";
import { trackViewBundleLoad } from "../performance-telemetry/bundle-load-tracking";

import type { LoadedView, ViewLoader, ViewLoaderConfig, ViewLoadError, LoadViewOptions } from "./types";

/**
 * Build the CDN URL for a view bundle.
 *
 * Pattern: `{cdnBaseUrl}/views/{viewId}/shell-{shellVersion}/{version}.js`
 *
 * Mirrors the server's view-bundle resolution
 * (`enrollment-composer/server/tinkertoy/tinkertoy.server.ts`) so client and
 * server import byte-identical bundles.
 */
export function resolveViewCdnUrl(
  viewId: ViewID,
  version: VersionString,
  config: ViewLoaderConfig,
): string {
  return `${config.cdnBaseUrl}/views/${viewId}/shell-${config.shellVersion}/${version}.js`;
}

/**
 * Structural runtime guard for the exported view definition. Kept minimal —
 * enough to catch a bundle that exports the wrong thing — because the full
 * `AnyView` surface (typed functions, generics) can't be validated at runtime.
 * A `z.custom` guard (rather than a `z.object`) preserves the live function
 * references on the returned object without a type assertion.
 */
function isRenderableView(value: unknown): value is AnyView {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (!("id" in value) || typeof value.id !== "string") {
    return false;
  }
  if (!("modules" in value) || typeof value.modules !== "function") {
    return false;
  }
  return true;
}

const ViewBundleSchema = z.object({
  view: z.custom<AnyView>(isRenderableView),
});

function makeViewLoadError(
  viewId: ViewID,
  version: VersionString,
  cdnUrl: string,
  variant:
    | { code: "NETWORK_ERROR"; cause: Error }
    | { code: "INVALID_VIEW"; reason: string }
    | { code: "IMPORT_ERROR"; cause: Error },
): ViewLoadError {
  return { viewId, version, cdnUrl, ...variant };
}

/**
 * Create a stateless view loader bound to a CDN base URL and shell version.
 *
 * The sibling of {@link import("../module-loader").createModuleLoader}: both
 * import a hashed ES-module bundle from the CDN and validate its export; they
 * share the dynamic-import + error-classification core via `importBundle` and
 * differ only in the `views/` path segment and the `view` export they expect.
 *
 * @example
 * ```ts
 * import { SHELL_VERSION } from "@bestowinc/tinkertoy-shell-deps";
 *
 * const loader = createViewLoader({
 *   cdnBaseUrl: "https://cdn.example.com",
 *   shellVersion: SHELL_VERSION,
 * });
 *
 * const { definition } = await loader.loadView("enrollment/get-started", "1.0.0");
 * ```
 */
export function createViewLoader(config: ViewLoaderConfig): ViewLoader {
  const importFn = config.__importFn ?? importBundleModule;

  async function loadView(
    viewId: ViewID,
    version: VersionString,
    options?: LoadViewOptions,
  ): Promise<LoadedView> {
    const cacheMode =
      options?.cacheMode ??
      (config.bundleOverrides?.[viewId] ? "dev_override" : "cold");

    return trackViewBundleLoad(
      { viewId, version, cacheMode },
      async () => loadViewInner(viewId, version),
    );
  }

  async function loadViewInner(
    viewId: ViewID,
    version: VersionString,
  ): Promise<LoadedView> {
    // A dev override (from the `ttv2-dev-override` cookie) points the browser at
    // a locally-served bundle for this view; otherwise resolve the CDN URL.
    const cdnUrl =
      config.bundleOverrides?.[viewId] ??
      resolveViewCdnUrl(viewId, version, config);

    let namespace: Record<string, unknown>;
    try {
      namespace = await importBundle(cdnUrl, importFn);
    } catch (err) {
      if (err instanceof BundleImportError) {
        const cause =
          err.cause instanceof Error ? err.cause : new Error(String(err.cause));
        throw makeViewLoadError(viewId, version, cdnUrl, {
          code: err.code,
          cause,
        });
      }
      throw err;
    }

    const parsed = ViewBundleSchema.safeParse(namespace);
    if (!parsed.success) {
      throw makeViewLoadError(viewId, version, cdnUrl, {
        code: "INVALID_VIEW",
        reason:
          'Bundle does not export a valid view definition as "view". ' +
          `Exports found: [${Object.keys(namespace).join(", ")}]. ` +
          `Validation: ${parsed.error.message}`,
      });
    }

    if (parsed.data.view.id !== viewId) {
      throw makeViewLoadError(viewId, version, cdnUrl, {
        code: "INVALID_VIEW",
        reason: `View ID mismatch: requested "${viewId}" but bundle exports "${parsed.data.view.id}"`,
      });
    }

    return { definition: parsed.data.view, cdnUrl };
  }

  return { loadView };
}
